/**
 * Regras de negócio para o **número** do imóvel (deliveryAddress.numero / campo Número).
 * Fastchip permite endereço manual; Correios e cadastro TIM aceitam "0" e "S/N".
 *
 * Para o **nome** da via (deliveryAddress.endereco / campo Endereço), use
 * `deliveryAddressStreetName.ts` — logradouro deve aceitar dígitos (ex.: "Rua 7 de Setembro").
 */

const MAX_STREET_NUMBER_LENGTH = 30;

/** Normaliza espaços e barras para detectar variantes de sem número. */
function compactSemNumeroToken(raw: string): string {
  return raw.replace(/\s+/g, '').replace(/-/g, '/').toUpperCase();
}

/**
 * Converte valor vindo de input, API ou Receita para string (preserva "0").
 */
export function coerceDeliveryAddressStreetNumber(
  value: string | number | null | undefined
): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  return String(value);
}

/** Indica se há número informado (inclui "0" e "S/N"). */
export function isFilledDeliveryAddressStreetNumber(
  value: string | number | null | undefined
): boolean {
  return coerceDeliveryAddressStreetNumber(value).trim().length > 0;
}

/**
 * Formato canônico enviado à API: trim, variantes de sem número → "S/N".
 */
export function normalizeDeliveryAddressStreetNumberForApi(
  value: string | number | null | undefined
): string {
  const raw = coerceDeliveryAddressStreetNumber(value).trim();
  if (!raw) return '';

  const compact = compactSemNumeroToken(raw.replace(/\./g, ''));
  if (compact === 'S/N' || compact === 'SN') {
    return 'S/N';
  }

  return raw;
}

/** Valida comprimento e presença após normalização leve. */
export function isValidDeliveryAddressStreetNumber(
  value: string | number | null | undefined
): boolean {
  const normalized = normalizeDeliveryAddressStreetNumberForApi(value);
  if (!normalized) return false;
  return normalized.length <= MAX_STREET_NUMBER_LENGTH;
}

export const DELIVERY_ADDRESS_STREET_NUMBER_HINT =
  'Use o número do imóvel, ou 0 / S/N quando não houver número.';

export const DELIVERY_ADDRESS_STREET_NUMBER_EMPTY_ERROR =
  'Informe o número (use 0 ou S/N se não houver número).';

export const DELIVERY_ADDRESS_STREET_NUMBER_INVALID_ERROR = `Número inválido (máx. ${MAX_STREET_NUMBER_LENGTH} caracteres).`;
